/**
 * Jambonz browser softphone — staff answer/place SIP calls inside Pulse.
 *
 * Wraps `@jambonz/client-sdk-web` (the jambonz WebRTC SDK). Credentials are
 * fetched per-staff from `GET /api/voice/sip/softphone-credentials`, which
 * provisions a Jambonz SIP client and returns fresh creds + the SBC WS URL.
 *
 * INSTALL (not yet in package.json): `npm i @jambonz/client-sdk-web`
 *
 * ⚠️ NOT yet exercised against a live Jambonz/browser. The SDK surface used
 * here (createJambonzClient / connect / 'incoming' / call.answer|hangup|mute /
 * call() / callConference()) is per the SDK README — verify on first
 * registration; the WS URL/port (:8443) is the jambonz.cloud default.
 *
 * This is the transport-specific layer only; the existing IncomingCallToast /
 * InCallPanel UI should drive it through the small interface below so the call
 * UX stays shared with the Twilio path.
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — dep added separately (see INSTALL above); no bundled types yet.
import { createJambonzClient } from '@jambonz/client-sdk-web';

import { api } from '../../../lib/api/client';

export interface SoftphoneCredentials {
  username: string;
  password: string;
  domain: string;
  ws_url: string;
  caller_id?: string; // store DID, used as caller-ID on outbound dials
}

/** A live call handle (subset of the SDK's JambonzCall we rely on). */
export interface SoftphoneCall {
  answer(): void;
  hangup(): void;
  mute(): void;
  unmute(): void;
  toggleMute(): void;
  hold(): void;
  unhold(): void;
  /** Blind transfer to a SIP URI or phone number; this call ends on success. */
  transfer(target: string, options?: unknown): void;
  readonly from?: string;
}

export type SoftphoneState = 'idle' | 'connecting' | 'registered' | 'error';

type Handlers = {
  onIncoming?: (call: SoftphoneCall) => void;
  onOutgoing?: (call: SoftphoneCall) => void;
  onStateChange?: (state: SoftphoneState) => void;
  onError?: (err: unknown) => void;
};

export class JambonzSoftphone {
  private client: any = null;
  private handlers: Handlers = {};
  private state: SoftphoneState = 'idle';
  private callerId?: string;
  private domain?: string;

  /** SIP realm we're registered to — for building `sip:staff-<id>@<domain>`. */
  getDomain() {
    return this.domain;
  }
  /** SIP URI for another staff member (blind-transfer target). */
  staffUri(staffId: string) {
    return this.domain ? `sip:staff-${staffId}@${this.domain}` : '';
  }

  setHandlers(h: Handlers) {
    this.handlers = { ...this.handlers, ...h };
  }

  getState() {
    return this.state;
  }

  private setState(s: SoftphoneState) {
    this.state = s;
    this.handlers.onStateChange?.(s);
  }

  /** Fetch credentials, connect, and register with the Jambonz SBC. */
  async connect(): Promise<void> {
    if (this.client) return;
    this.setState('connecting');
    try {
      const creds = await api.get<SoftphoneCredentials>('/voice/sip/softphone-credentials');
      this.callerId = creds.caller_id || undefined;
      this.domain = creds.domain || undefined;
      this.client = createJambonzClient({
        server: creds.ws_url,
        username: creds.username,
        password: creds.password,
        realm: creds.domain,
        autoRegister: true,
      });
      this.client.on('incoming', (call: SoftphoneCall) => this.handlers.onIncoming?.(call));
      this.client.on?.('error', (e: unknown) => {
        this.setState('error');
        this.handlers.onError?.(e);
      });
      await this.client.connect();
      this.setState('registered');
    } catch (err) {
      this.setState('error');
      this.handlers.onError?.(err);
      throw err;
    }
  }

  /** Place an outbound call to a phone number / SIP URI (caller-ID = store DID). */
  async dial(target: string): Promise<SoftphoneCall | undefined> {
    const opts = this.callerId ? { callerId: this.callerId } : undefined;
    const call: SoftphoneCall | undefined = await this.client?.call(target, opts);
    if (call) this.handlers.onOutgoing?.(call);
    return call;
  }

  /** Join (or create) a conference room — caller is moved in server-side. */
  async joinConference(name: string): Promise<SoftphoneCall | undefined> {
    return this.client?.callConference(name);
  }

  disconnect(): void {
    try {
      this.client?.disconnect?.();
    } catch {
      /* ignore */
    }
    this.client = null;
    this.setState('idle');
  }
}

/** Module-level singleton — one registration per staff browser session. */
export const softphone = new JambonzSoftphone();
